import { defineStore } from 'pinia'
import { commonGetters } from '@/stores/commonGetters.js'
import { showInAppAndDesktopNotification } from '@/lib/showNotification'
import {
  dedupeAlerts,
  normalizeAlert,
  isApprovalAlert,
  isOpenAlert,
  sortAlertsByPriority,
} from '@/lib/alerts'

const DASHBOARD_LIMIT = 4

export const useAlertsStore = defineStore('alerts', {
  state: () => ({
    items: [],
    loading: false,
    error: null,
    lastFetchedAt: null,
    /** Known ids used to detect newly arrived alerts for toasting */
    knownIds: [],
    /** Suppress toasts on the first successful fetch of a workspace session */
    hasHydrated: false,
  }),

  getters: {
    ...commonGetters,

    /** All alerts, priority-sorted */
    alerts: (state) => sortAlertsByPriority(state.items),

    dangerCount: (state) =>
      state.items.filter((a) => a.severity === 'danger').length,

    warningCount: (state) =>
      state.items.filter((a) => a.severity === 'warning').length,

    infoCount: (state) =>
      state.items.filter((a) => a.severity === 'info').length,

    totalCount: (state) => state.items.length,

    /** Expired/overdue items for the dashboard "Open Alerts" counters. */
    openAlertsCount: (state) => state.items.filter(isOpenAlert).length,

    /** Pending approvals for the dashboard "Items to Approve" row. */
    approvalAlertsCount: (state) => state.items.filter(isApprovalAlert).length,

    /** Approval alerts, priority-sorted (action list CTAs). */
    approvalAlerts: (state) => sortAlertsByPriority(state.items.filter(isApprovalAlert)),

    /** Top N for the dashboard "Needs Attention" card */
    topAlerts: (state) =>
      sortAlertsByPriority(state.items).slice(0, DASHBOARD_LIMIT),

    hasAlerts: (state) => state.items.length > 0,

    bySeverity: (state) => (severity) =>
      sortAlertsByPriority(state.items.filter((a) => a.severity === severity)),

    byScope: (state) => (scope) =>
      sortAlertsByPriority(state.items.filter((a) => a.scope === scope)),
  },

  actions: {
    /**
     * Merge a fetched alerts response (workspace + user alerts) with the dashboard score flags and
     * the live (Echo) items. The request itself is owned by `useAlertsFeed` (`@/queries/alerts`).
     * @param {Object} body - the `{ message, data }` response of the alerts endpoint
     * @param {Object} [options]
     * @param {Array} [options.scoreFlags] - Dashboard score flags to merge
     * @param {boolean} [options.notify] - Toast newly seen ids after hydration
     */
    applyFetched(body, options = {}) {
      const { scoreFlags = null, notify = true } = options
      const workspaceId = this.workspace_id
      if (!workspaceId) {
        this.reset()
        return []
      }

      this.error = null

      const data = body?.data ?? {}
      const rawWorkspace = data.workspace_alerts ?? data.alerts ?? []
      const rawUser = data.user_alerts ?? []

      const fromApi = [
        ...rawWorkspace.map((a) =>
          normalizeAlert(
            { ...a, scope: a.scope ?? 'workspace' },
            { workspaceId, source: 'alert' },
          ),
        ),
        ...rawUser.map((a) =>
          normalizeAlert(
            { ...a, scope: a.scope ?? 'user' },
            { workspaceId, source: 'alert' },
          ),
        ),
      ].filter(Boolean)

      const fromFlags = Array.isArray(scoreFlags)
        ? scoreFlags
            .map((flag, index) =>
              normalizeAlert(
                {
                  ...flag,
                  id: flag.id ?? `score-flag-${flag.type ?? index}`,
                  scope: 'workspace',
                },
                { workspaceId, source: 'score_flag' },
              ),
            )
            .filter(Boolean)
        : this.items.filter((a) => a.source === 'score_flag')

      const merged = sortAlertsByPriority(
        dedupeAlerts([...fromApi, ...fromFlags]),
      )

      if (notify) this._toastNewAlerts(merged)

      this.items = merged
      this.knownIds = merged.map((a) => a.id)
      this.hasHydrated = true
      this.lastFetchedAt = Date.now()
      return this.alerts
    },

    /** Records a failed alerts request (the feed keeps showing the last good list). */
    setFetchError(err) {
      this.error = err?.message ?? 'Failed to load alerts'
      if (err?.response?.status !== 404) {
        console.warn('[alerts] fetch failed', err)
      }
    },

    /**
     * Merge dashboard score flags into the alert list without an API round-trip.
     */
    syncScoreFlags(scoreFlags = []) {
      const workspaceId = this.workspace_id
      if (!workspaceId) return

      const flags = (scoreFlags ?? [])
        .map((flag, index) =>
          normalizeAlert(
            {
              ...flag,
              id: flag.id ?? `score-flag-${flag.type ?? index}`,
              scope: 'workspace',
            },
            { workspaceId, source: 'score_flag' },
          ),
        )
        .filter(Boolean)

      const others = this.items.filter((a) => a.source !== 'score_flag')
      const merged = sortAlertsByPriority(dedupeAlerts([...others, ...flags]))

      this.items = merged
      this.knownIds = [...new Set([...this.knownIds, ...merged.map((a) => a.id)])]
      this.hasHydrated = true
    },

    /**
     * Ingest a real-time alert (Echo). Shows toast and prepends if new.
     */
    ingestAlert(payload, { notify = true } = {}) {
      const workspaceId = this.workspace_id
      const alert = normalizeAlert(payload, {
        workspaceId,
        source: payload?.source ?? 'alert',
      })
      if (!alert) return null

      const existing = this.items.find((a) => a.id === alert.id)
      if (existing) {
        this.items = sortAlertsByPriority(
          this.items.map((a) => (a.id === alert.id ? { ...a, ...alert } : a)),
        )
        return existing
      }

      this.items = sortAlertsByPriority([alert, ...this.items])
      this.knownIds = [...new Set([alert.id, ...this.knownIds])]

      if (notify && this.hasHydrated) {
        this._notify(alert)
      }

      return alert
    },

    dismiss(id) {
      this.items = this.items.filter((a) => a.id !== id)
      this.knownIds = this.knownIds.filter((knownId) => knownId !== id)
    },

    reset() {
      this.items = []
      this.loading = false
      this.error = null
      this.lastFetchedAt = null
      this.knownIds = []
      this.hasHydrated = false
    },

    _toastNewAlerts(nextItems) {
      if (!this.hasHydrated) return

      const known = new Set(this.knownIds)
      const newcomers = nextItems.filter((a) => !known.has(a.id))
      // Cap toast spam: at most 3 toasts per refresh
      newcomers.slice(0, 3).forEach((alert) => this._notify(alert))
    },

    _notify(alert) {
      showInAppAndDesktopNotification({
        title: alert.title,
        message: alert.message,
        type:
          alert.severity === 'danger'
            ? 'warning'
            : alert.severity === 'warning'
              ? 'warning'
              : 'info',
        link: typeof alert.link === 'string' ? alert.link : null,
      })
    },
  },
})
